import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  ButtonBase,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@material-ui/core';
import { useSelector } from 'react-redux';
import { AddIcon, CheckIcon, EditIcon } from '../../../../components/Icon';
import { eadmissionAPI } from '../../../../apis';
import StyleButton from '../../../../components/StyleButton';
import SettingAddPop from './SettingAddPop';
import SettingEditPop from './SettingEditPop';

const useStyles = makeStyles(() => ({
  tableContainer: {
    height: '65vh',
  },
  table: {
    position: 'relative',
    minWidth: '45rem',
    '& .MuiTableCell-root': {
      border: 'none',
      fontSize: '1.125rem',
      fontWeight: '500',
      lineHeight: '1.675rem',
      color: '#2e4469',
      padding: '0 1rem ',
    },
    '& .MuiTableHead-root': {
      position: 'sticky',
      top: '0',
      left: '0',
      zIndex: '10',
      '& .MuiTableRow-root': {
        backgroundColor: '#fff',
        textTransform: 'uppercase',
      },
      '& .MuiTableCell-root': {
        fontWeight: 'bold',
        color: '#538fff',
        padding: '1rem',
        backgroundColor: '#fff',
        fontSize: '0.875rem',
      },
      '& .MuiTableCell-root:nth-child(1)': {
        paddingLeft: '1.313rem',
      },
      '& .MuiTableCell-root:nth-last-child(1)': {
        paddingRight: '1.883rem',
      },
    },
    '& .MuiTableBody-root': {
      '& :hover': {
        backgroundColor: '#f5faff',
      },
      '& > .MuiTableRow-root': {
        borderBottom: '1px solid #E6E8E9',
      },
      '& .MuiTableCell-root': {
        height: '3.5rem',
        fontFamily: 'NotoSans-Medium',
      },
      '& .MuiTableCell-root:nth-child(1)': {
        paddingLeft: '1.313rem',
      },
      '& .MuiTableCell-root:nth-last-child(1)': {
        paddingRight: '1.883rem',
      },
    },
  },
  checkIcon: {
    width: '1.25rem',
    height: '1.25rem',
    color: '#28d2ab',
  },
  editBtn: {
    width: '2.25rem',
    height: '2.25rem',
    minWidth: '0',
    backgroundColor: '#4c93ff',
    borderRadius: '0.625rem',
    color: '#fff',
    '&:hover': {
      background: '#4c93ff !important',
    },
  },
  editIcon: {
    fontSize: '1rem',
    '&:hover': {
      background: '#4c93ff !important',
    },
  },
}));

const RemarkList = ({ tabValue }) => {
  const classes = useStyles();
  const [addPopOpen, setAddPopOpen] = useState(false);
  const [editPopOpen, setEditPopOpen] = useState(false);
  const [editState, setEditState] = useState({
    id: '',
    input: '',
    active: '',
    version: '',
  });
  const tableRef = useRef();
  const pageSize = 20;
  const [pageInfo, setPageInfo] = useState({
    pageNo: 1,
    pageSize,
  });
  const [total, setTotal] = useState(0);
  const [list, setList] = useState([]);
  const rouList = useSelector((state) => state.rouList);

  const remarkKey = useMemo(() => {
    switch (tabValue) {
      case 'Emergency Admission Remarks':
        return 'remark';
      case 'Special Alert':
        return 'specialAlert';
      case 'Specialty Remark':
        return 'specialtyRemark';
      default:
        return '';
    }
  }, [tabValue]);

  const idKey = useMemo(() => {
    switch (tabValue) {
      case 'Emergency Admission Remarks':
        return 'remarkId';
      case 'Special Alert':
        return 'specialAlertId';
      case 'Specialty Remark':
        return 'specialtyRemarkId';
      default:
        return '';
    }
  }, [tabValue]);

  const handleEditOpen = (eid, remarkInput, estatus, version) => {
    setEditState({
      id: eid,
      input: remarkInput,
      active: estatus,
      version: version,
    });
    setEditPopOpen(true);
  };

  const getRemarkList = useCallback(async () => {
    try {
      let apiPath = '';
      switch (tabValue) {
        case 'Emergency Admission Remarks':
          apiPath = 'getRemarkList';
          break;
        case 'Special Alert':
          apiPath = 'getSpecialAlertList';
          break;
        case 'Specialty Remark':
          apiPath = 'getSpecialtyRemarkList';
          break;

        default:
          break;
      }
      const { result } = await eadmissionAPI[apiPath]({ ...pageInfo });
      setTotal(result.count);
      if (pageInfo.pageNo > 1) {
        setList((o) => [...o, ...result.data]);
      } else {
        setList(result.data);
      }
    } catch (error) {
      console.log(error);
    }
  }, [pageInfo, tabValue]);

  useEffect(() => {
    getRemarkList();
  }, [getRemarkList, rouList]);

  // 翻页
  const turnPages = useCallback(() => {
    if (pageInfo.pageNo * pageInfo.pageSize < total) {
      setPageInfo((o) => ({
        ...o,
        pageNo: o.pageNo + 1,
      }));
    }
  }, [total, pageInfo]);

  const scrollHandler = useCallback(() => {
    if (tableRef) {
      const { scrollTop, clientHeight, scrollHeight } = tableRef.current;

      if (scrollTop + clientHeight + 1 >= scrollHeight) {
        turnPages();
      }
    }
  }, [turnPages]);

  const handleAdd =
    ({ remark, status, handleClose }) =>
    async () => {
      try {
        let apiPath = '';
        switch (tabValue) {
          case 'Emergency Admission Remarks':
            apiPath = 'createRemark';
            break;
          case 'Special Alert':
            apiPath = 'createSpecialAlert';
            break;
          case 'Specialty Remark':
            apiPath = 'createSpecialtyRemark';
            break;

          default:
            break;
        }
        await eadmissionAPI[apiPath]({
          [remarkKey]: remark,
          status,
        });
        handleClose();
        if (pageInfo.pageNo === 1) {
          getRemarkList();
        } else {
          setPageInfo({
            pageNo: 1,
            pageSize,
          });
        }
      } catch {}
    };

  const handleEdit =
    ({ remark, status, version, id, handleClose }) =>
    async () => {
      try {
        let apiPath = '';
        switch (tabValue) {
          case 'Emergency Admission Remarks':
            apiPath = 'updateRemark';
            break;
          case 'Special Alert':
            apiPath = 'updateSpecialAlert';
            break;
          case 'Specialty Remark':
            apiPath = 'updateSpecialtyRemark';
            break;

          default:
            break;
        }
        await eadmissionAPI[apiPath]({
          [remarkKey]: remark,
          status: status,
          [idKey]: id,
          version,
        });
        handleClose();
        if (pageInfo.pageNo === 1) {
          getRemarkList();
        } else {
          setPageInfo({
            pageNo: 1,
            pageSize,
          });
        }
      } catch {}
    };

  return (
    <>
      <Box display="flex" justifyContent="end">
        <StyleButton text="Add" Icon={AddIcon} type="fill" width="5.6875rem" onClick={() => setAddPopOpen(true)} />
      </Box>
      <Box overflow="auto">
        <TableContainer className={classes.tableContainer} ref={tableRef} onScroll={scrollHandler}>
          <Table className={classes.table}>
            <TableHead>
              <TableRow>
                <TableCell align="left">Remarks content</TableCell>
                <TableCell align="center">Active</TableCell>
                <TableCell align="right">Edit</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {list.map((item) => (
                <TableRow
                  key={item.remarkId ?? item.specialAlertId}
                  onDoubleClick={() => handleEditOpen(item?.[idKey], item?.[remarkKey], item.status, item.version)}
                >
                  <TableCell align="left">{item?.[remarkKey]}</TableCell>
                  <TableCell align="center">
                    {item.status === 1 && <CheckIcon className={classes.checkIcon} />}
                  </TableCell>
                  <TableCell align="right">
                    <ButtonBase
                      className={classes.editBtn}
                      onClick={() => handleEditOpen(item?.[idKey], item?.[remarkKey], item.status, item.version)}
                    >
                      <EditIcon className={classes.editIcon} />
                    </ButtonBase>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
      {addPopOpen && (
        <SettingAddPop
          handleClose={() => setAddPopOpen(false)}
          confirm={() => {
            if (pageInfo.pageNo === 1) {
              getRemarkList();
            } else {
              setPageInfo({
                pageNo: 1,
                pageSize,
              });
            }
          }}
          tabValue={tabValue}
          handleAdd={handleAdd}
        />
      )}
      {editPopOpen && (
        <SettingEditPop
          handleClose={() => setEditPopOpen(false)}
          eid={editState.id}
          remarkInput={editState.input}
          estatus={editState.active}
          confirm={() => {
            if (pageInfo.pageNo === 1) {
              getRemarkList();
            } else {
              setPageInfo({
                pageNo: 1,
                pageSize,
              });
            }
          }}
          version={editState.version}
          handleEdit={handleEdit}
          tabValue={tabValue}
        />
      )}
    </>
  );
};

export default RemarkList;
